// The queue remembers which scope each write belongs to and compares it against the current one
// before sending it. The request itself names that scope as well - see `scopeHeaders` below.
// redux-offline's peek/dequeue are synchronous, hence a plain copy rather than a session lookup.
import { onSessionChange } from '../session';

// This tab's scope, deliberately. `onSessionChange` publishes in-process, so a tenant switch in
// another tab is not heard here - and should not be: the user of this tab is still working in the
// tenant this tab shows, so that is the one its new writes belong to.
let activeTenantId = null;
let activeUseCaseId = null;

/**
 * The tenant the user is currently working in, or null when it is not known yet - between logout
 * and the next tenant selection, for instance.
 * @returns {string|null}
 */
export function getActiveTenantId() {
    return activeTenantId;
}

/**
 * The use case the user is currently working in, or null when it is not known yet.
 *
 * The use case matters as much as the tenant: the server picks a write's unique-key fields
 * (`RELATIONS_UNIQUE_KEY_FIELDS[useCaseTitle]`), its feature flags and its repository override off
 * `activeTenant.useCase` in the token, so a write replayed under a sibling use case of the same
 * tenant is processed under the wrong rules.
 * @returns {string|null}
 */
export function getActiveUseCaseId() {
    return activeUseCaseId;
}

/**
 * A tenant is `identity:tenant` on the session in current data and a bare record in older data -
 * the same fallback the rest of the app uses.
 */
function toTenantId(tenant) {
    const id = tenant?.['identity:tenant']?._id ?? tenant?._id;
    return id == null ? null : String(id);
}

/**
 * A use case is `metaui:useCase` on the session, with the same older-data fallback as the tenant.
 */
function toUseCaseId(useCase) {
    const id = useCase?.['metaui:useCase']?._id ?? useCase?._id;
    return id == null ? null : String(id);
}

/**
 * The headers that tell the server which tenant and use case a request is for. `addToken` fills
 * them from the session when a request does not supply them, and the session is shared by every
 * tab on this origin - so a queued write that relies on it is sent to whichever tenant another tab
 * last switched to (GH #1698). A queued write supplies its own instead.
 *
 * Both or neither: with only one, `addToken` would take the other from the session, pairing one
 * scope's tenant with another's use case.
 * @param {{tenantId?: string, useCaseId?: string}} [scope]
 * @returns {{'sst-active-tenant-id'?: string, 'sst-app-id'?: string}}
 */
export function scopeHeaders(scope) {
    const { tenantId, useCaseId } = scope ?? {};
    return tenantId && useCaseId ? { 'sst-active-tenant-id': tenantId, 'sst-app-id': useCaseId } : {};
}

/**
 * Copy the tenant and use case off a session the caller already holds. Needed as well as the
 * subscription below, because `session.init()` returns the session without publishing it - on a
 * reload nothing is published at all.
 */
export function setActiveScopeFromSession(session) {
    activeTenantId = toTenantId(session?.tenant);
    activeUseCaseId = toUseCaseId(session?.useCase);
    return { tenantId: activeTenantId, useCaseId: activeUseCaseId };
}

onSessionChange((type, session) => {
    if (type === 'destroySession') {
        activeTenantId = null;
        activeUseCaseId = null;
        return;
    }
    if (type === 'session') {
        activeTenantId = toTenantId(session?.tenant);
        activeUseCaseId = toUseCaseId(session?.useCase);
    }
});

export const _private = {
    toTenantId,
    toUseCaseId,
    setActiveTenantId: id => {
        activeTenantId = id;
    },
    setActiveUseCaseId: id => {
        activeUseCaseId = id;
    }
};
